import JSEncrypt from 'jsencrypt';
// 组件
import { ProModal } from "../antd";
// 方法
import { NavigateFunction } from 'react-router-dom';
// 常量
import { ACCESS_TOKEN_KEY, ADMIN_APPLICATION_INFO_KEY, APPLICATION_INFO_KEY, APP_PATHNAME_LIST_KEY, EXPIRATION_TIME, HISTORY_URL_KEY, REFRESH_TOKEN_KEY, TENANT_ID_KEY, VERSION_RECORD_KEY } from "../constants/localStorage";
import { tokenTypeEnum } from "../constants/enum";
import { LOGIN_URL } from "../constants/constant";
import { getMainWindow } from "./utils";
import { IObject } from "../constants/interface";
import { PASSWORD_STRENGTH_ENUM } from '../constants/dict.enum';
import { IWatermarkConfig, WatermarkMenuItem } from '../PageLayout/context/LayoutContext';

/**
 * 设置登录信息
 * @param access_token token
 * @param refresh_token 刷新token令牌
 * @param expires 过期时间
 */
export function setToken(
  access_token: string,
  refresh_token: string,
  expires: number | string,
) {
  const expirationTime: number =
    parseInt((new Date().getTime() / 1000).toString(), 10) + (+expires);
  window.localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  window.localStorage.setItem(EXPIRATION_TIME, expirationTime.toString());
}
/**
 * 清除登录信息
 */
export function clearAuthority() {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  window.localStorage.removeItem(EXPIRATION_TIME);
  window.localStorage.removeItem(APP_PATHNAME_LIST_KEY);
  window.localStorage.removeItem(ADMIN_APPLICATION_INFO_KEY);
  window.localStorage.removeItem(HISTORY_URL_KEY);
  window.sessionStorage.clear();
}
/**
 * 匹配租户信息
 * @param isEqualUser 本次用户和缓存用户是否为同一用户
 * @param tenantList 租户列表
 * @param navigate 路由跳转方法
 */
export function checkTenant(isEqualUser: boolean, tenantList?: any, navigate?: NavigateFunction) {
  if (Array.isArray(tenantList) && tenantList.length) {
    // 只有一个租户的情况
    if (tenantList.length === 1) {
      window.localStorage.setItem(TENANT_ID_KEY, tenantList[0].tenantId);
      return tenantList[0];
    }
    // 多条租户
    if (isEqualUser) {
      // 最近两次登录的用户相同 + 匹配到缓存中的租户信息 = 保留上一次的租户信息
      /** 缓存中的租户信息 */
      const history_tenant = window.localStorage.getItem(TENANT_ID_KEY);
      const findItem = tenantList?.find(
        (v: any) => v.tenantId === history_tenant,
      );
      if (findItem) return findItem;
    }
    navigate?.('/authView/tenant', { replace: true, });
    return false;
  }
  const pathname = window.location.pathname;
  if (pathname.startsWith('/userView')) {
    navigate?.('/userView/userCenter', { replace: true, });
  } else {
    window.location.href = `${window.location.origin}/userView/userCenter`;
  }
  return false;
}
/**
 * 匹配应用信息
 * @param isEqualUser 本次用户和缓存用户是否为同一用户
 * @param tenantName 租户名称
 * @param appList 应用列表
 * @param navigate 路由跳转方法
 * @returns 
 */
export function checkApp(
  isEqualUser: boolean,
  tenantName: string,
  appList?: any,
  navigate?: NavigateFunction
) {
  if (Array.isArray(appList) && appList.length) {
    // 只有一个应用的情况
    if (appList.length === 1) {
      const currentApp = appList[0];
      window.localStorage.setItem(APPLICATION_INFO_KEY, setAppLicationInfo(currentApp));
      return currentApp;
    }
    // 历史路径缓存
    const historyPath = window.localStorage.getItem(HISTORY_URL_KEY);
    if (historyPath) {
      const formatPath = formatHistoryPath(historyPath);
      if (formatPath.pathname && formatPath.router) {
        const findItem = appList.find(item => item.urlPrefix === formatPath.pathname);
        if (findItem) {
          window.localStorage.setItem(APPLICATION_INFO_KEY, setAppLicationInfo(findItem));
          return findItem;
        }
      }
    }
    // 多个应用
    if (appList.length > 1 && isEqualUser) {
      const historyAppInfo = getAppLicationInfo();
      if (historyAppInfo.id || historyAppInfo.code) {
        const findItem = appList?.find(
          (v: any) => v.systemId === historyAppInfo.id || v.systemCode === historyAppInfo.code,
        );
        if (findItem) {
          window.localStorage.setItem(APPLICATION_INFO_KEY, setAppLicationInfo(findItem));
          return findItem;
        }
      }
    }
    navigate?.('/authView/tenant', { replace: true, });
    return false;
  }
  // 当前租户未绑定应用
  ProModal.warning({
    title: `当前租户[${tenantName}]未绑定应用`,
    content: '请联系管理员为当前租户绑定应用',
  });
  return false;
}
/**
 * 验证版本更新是否曾经显示
 * @param userAccountId 用户ID
 * @param systemId 应用ID
 * @param systemVersionId 版本ID
 * @returns 
 */
export function verifyVersionRecord(userAccountId?: string, systemId?: string, systemVersionId?: string): boolean {
  if (!userAccountId || !systemId || !systemVersionId) return false;
  const versionInfo = window.localStorage.getItem(VERSION_RECORD_KEY);
    if (versionInfo) {
      try {
        const versionList = JSON.parse(versionInfo);
        const findItem = versionList?.find?.((item: any) => {
          return item.systemId === systemId && item.userAccountId === userAccountId && item.systemVersionId === systemVersionId;
        })
        return !!findItem;
      } catch (errorInfo) {
        return false;
      }
    }
  return false;
}
/**
 * 设置用户已浏览版本信息
 * @param userAccountId 用户ID
 * @param systemId 应用ID
 * @param systemVersionId 版本ID
 * @returns 
 */
export function setVersionRecord(userAccountId?: string, systemId?: string, systemVersionId?: string) {
  if (!userAccountId || !systemId || !systemVersionId) return;
  const versionInfo = window.localStorage.getItem(VERSION_RECORD_KEY);
  let versionList: any[] = [];
  if (versionInfo) {
    try {
      versionList = JSON.parse(versionInfo);
    } catch (errorInfo) {
      versionList = [];
    }
  }
  versionList.push({
    userAccountId,
    systemId,
    systemVersionId,
  });
  window.localStorage.setItem(VERSION_RECORD_KEY, JSON.stringify(versionList));
}
/**
 * 获取主工程应用信息
 * @param isApp 是否读取项目内配置的应用常量
 * @returns 
 */
export function getMainSystemCode(isApp: boolean = true): {id?: string, code?: string} {
  if (isApp && window._appCode) return window._appCode;
  const pathnameObj = JSON.parse(window.localStorage.getItem(APP_PATHNAME_LIST_KEY) ?? '{}');
  const pathname = getMainWindow().location.pathname?.split?.('/')?.[1];
  if (pathname && pathnameObj[pathname]) return pathnameObj[pathname];
  return getAppLicationInfo();
}

/** 获取当前应用信息 */
export function getAppLicationInfo(): {id?: string, code?: string} {
  /** 是否为应用中心接口 */
  const isAppUrl = window.__IS_APP || window.parent?.__IS_APP;
  if (isAppUrl) {
    const appLicationInfo = JSON.parse(window.localStorage.getItem(ADMIN_APPLICATION_INFO_KEY) ?? '{}');
    return {
      id: appLicationInfo.id,
      code: appLicationInfo.code,
    };
  } else {
    const appLicationInfo = JSON.parse(window.localStorage.getItem(APPLICATION_INFO_KEY) ?? '{}');
    return {
      id: appLicationInfo.id,
      code: appLicationInfo.code,
    };
  }
}
/** 获取当前租户id */
export function getTenantId(): string {
  const tenantId = window.__tenantId || window.localStorage.getItem(TENANT_ID_KEY) || '';
  return tenantId;
}

/** 获取应用前缀 */
export function getApplicationPrefix(): string {
  let applicationList: any = window.localStorage.getItem(APP_PATHNAME_LIST_KEY);
  let appLicationInfo: any = window.localStorage.getItem(APPLICATION_INFO_KEY);
  if (applicationList && appLicationInfo) {
    applicationList = JSON.parse(applicationList);
    appLicationInfo = JSON.parse(appLicationInfo);
    for (let [key, value] of Object.entries<any>(applicationList)) {
      if (value?.id === appLicationInfo.id) {
        return key;
      }
    }
  }
  return '';
}

// 登录
export function goLogin() {
  const pathname = window.location.pathname;
  // const historyUrl = [
  //   'amazonOauth',
  //   'amazonAdvertisingOAuth',
  //   'shopifyAuth',
  //   'lianlianPay',
  //   'paypalAuth',
  // ];
  if (!window.__POWERED_BY_WUJIE__) {
    window.localStorage.setItem(HISTORY_URL_KEY, pathname);
  }
  const __window = window.__POWERED_BY_WUJIE__ ? window.parent.__window : window;
  if (__window?.location.href === LOGIN_URL) return;
  if (__window) {
    __window.location.href = LOGIN_URL;
  }
}
/** 设置当前应用信息 */
export function setAppLicationInfo(data: any): string {
  if (!data) return '';
  const newAppInfo = {code: data.systemCode, id: data.systemId};
  return JSON.stringify(newAppInfo);
}
/**
 * 验证登录信息
 * @param tokenDelay token多少分钟检查一次
 * @returns 
 */
export function verificationLogin(tokenDelay: number, minPromptTime: number): tokenTypeEnum {
  // 验证token和refreshToken是否存在
  const currentToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
  const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!currentToken || !refreshToken) return tokenTypeEnum.expire;
  // 验证是否存在过期时间
  const expirationTime = window.Number(
    window.localStorage.getItem(EXPIRATION_TIME),
  );
  if (!expirationTime) return tokenTypeEnum.expire;
  const currentTime = Math.floor(new Date().getTime() / 1000);
  const difTime = (expirationTime - currentTime) / 60;
  if (difTime < 0) return tokenTypeEnum.expire;
  // 验证过期时间是否处于临界值
  if (difTime > (tokenDelay + minPromptTime)) {
    return tokenTypeEnum.continuing;
  }
  return tokenTypeEnum.willExpire;
}
/**
 * 判断上一次时间戳距离当前时间戳是否在时间间隔内
 * @param beforeTime 上一次时间戳
 * @param interval 时间间隔
 * @return boolean
 */
export function judgeTimeInterval(beforeTime: string | number, interval: number): boolean {
  const prevTime: number = typeof beforeTime === 'string' ? Number(beforeTime) : beforeTime;
  const currentTime: number = Math.floor(new Date().getTime() / 1000);
  const difTime = (currentTime - prevTime) / 60;
  return difTime < interval;
}
/**
 * 格式化历史记录路径
 * @param historyPath 历史记录
 * @returns 
 */
export function formatHistoryPath(historyPath: string): {pathname: string, router: string} {
  const pathArr = historyPath.split('/');
  return {
    pathname: pathArr.length > 1 ? pathArr[1] : '',
    router: historyPath,
  };
}

/**
 * 获取当前应用信息
 * @param appList 应用列表
 * @returns
 */
export function checkApplication(appList?: any) {
  if (Array.isArray(appList) && appList.length) {
    if (appList.length === 1) {
      return appList[0];
    }
    const historyApp = getAppLicationInfo();
    if (historyApp.id || historyApp.code) {
      const findItem = appList?.find(
        (v: any) => v.systemId === historyApp.id || v.systemCode === historyApp.code,
      );
      if (findItem) return findItem;
    }
  }
  return false;
}
/**
 * 获取菜单权限列表
 * @param menuList 菜单列表
 * @returns 
 */
export function getAuthorityList(menuList?: any[]): {
  authorityList: string[];
  menuHelp: IObject;
} {
  const authorityList: string[] = [];
  let menuHelp: IObject = {};
  if (Array.isArray(menuList)) {
    menuList.forEach(list => {
      authorityList.push(list.resourceCode);
      if (list.helpUrl && list.path) {
        menuHelp[list.path] = list.helpUrl;
      }
      if (Array.isArray(list.buttons)) {
        authorityList.push(...list.buttons.map((v: any) => v.resourceCode));
      }
      if (Array.isArray(list.children)) {
        const childrenResult = getAuthorityList(list.children);
        authorityList.push(...(childrenResult.authorityList));
        menuHelp = { ...menuHelp, ...childrenResult.menuHelp};
      }
    });
  }
  return {
    authorityList,
    menuHelp,
  };
}
/** 处理水印配置 */
export function getWatermarkConfig(routes: any[], tenantSystemWatermarkResponse: any): IWatermarkConfig | undefined {
  if (!tenantSystemWatermarkResponse) return undefined;
  const {
    /** 水印文字类型 */
    contentType,
    /** 水印位置 */
    direction,
    /** 是否显示时间 */
    displayTimeState,
    /** 水印方向 */
    position,
    /** 水印状态 */
    watermarkStatus,
    /** 水印文字 */
    customContent,
    /** 水印菜单 */
    watermarkResourceList,
  } = tenantSystemWatermarkResponse;
  const watermarkMenu: WatermarkMenuItem[] = [];
  if (Array.isArray(watermarkResourceList) && watermarkResourceList.length) {
    watermarkResourceList.forEach(item => {
      const findItem = routes.find(v => v.resourceId === item.resourceId);
      if (findItem) {
        watermarkMenu.push({
          name: findItem.name,
          path: findItem.path,
          resourceId: findItem.resourceId,
        });
      }
    });
  }
  return {
    watermarkStatus,
    contentType,
    customContent,
    displayTimeState,
    direction,
    position,
    menu: watermarkMenu,
  };
};
/**
 * 密码评分
 * @param passwordStr 密码
 * @returns 
 */
export function checkPasswordLevel(passwordStr: string) {
  let result: number = 0;
  /** 密码长度评分 */
  const passwordLength: number = passwordStr.length;
  if (passwordLength === 0) {
    result += 0;
  } else if (passwordLength > 0 && passwordLength < 8) {
    result += 5;
  } else if (passwordLength > 10) {
    result += 20;
  } else {
    result += 10;
  }
  /** 大小写字母评分 */
  // 是否包含大小写字母中的一种
  let letterHave: boolean = false;
  // 是否包含大小写字母
  let letterAll: boolean = false;
  // 找到大写字母
  const capital = passwordStr.match(/[A-Z]{1}/);
  // 找到大写字母
  const small = passwordStr.match(/[a-z]{1}/);
  if (capital === null && small === null) {
    result += 0;
  } else if (capital !== null && small !== null) {
    result += 25;
    letterAll = true;
  } else {
    result += 10;
    letterHave = true;
  }
  /** 数字评分 */
  // 是否包含数字
  let numberHave: boolean = false;
  // 数字的个数
  let numberLength: number = 0;
  for (let i = 0; i < passwordLength; i++) {
    const currentCharAt = passwordStr.codePointAt(i);
    if (currentCharAt && currentCharAt >= 49 && currentCharAt <= 57) {
      numberHave = true;
      numberLength += 1;
    }
  }
  if (numberLength === 0) {
    result += 0;
  } else if (numberLength > 2) {
    result += 20;
  } else {
    result += 10;
  }
  /** 特殊字符评分 */
  // 是否包含特殊字符
  let otherHave: boolean = false;
  // 特殊字符长度
  let otherLength: number = 0;
  for (let i = 0; i < passwordLength; i++) {
    const currentCharAt = passwordStr.codePointAt(i);
    if (currentCharAt && (
      (currentCharAt >= 49 && currentCharAt <= 57) ||
      (currentCharAt >= 65 && currentCharAt <= 90) ||
      (currentCharAt >= 97 && currentCharAt <= 122)
    )) {
      continue;
    }
    otherHave = true;
    otherLength += 1;
  }
  if (otherLength === 0) {
    result += 0;
  } else if (otherLength > 1) {
    result += 20;
  } else {
    result += 10;
  }
  /** 额外奖励的评分 */
  if (letterAll && numberHave && otherHave) {
    result += 15;
  } else if (
    (letterHave && numberHave && otherHave) ||
    (letterAll && numberHave) ||
    (letterAll && otherHave)
  ) {
    result += 10;
  } else if (
    letterAll ||
    (letterHave && numberHave) ||
    (letterHave && otherHave) ||
    (numberHave && otherHave)
  ) {
    result += 5;
  }
  let levelNumber: number = 0;
  if (result > 75) {
    levelNumber = PASSWORD_STRENGTH_ENUM.VERY_STRONG;
  } else if (result > 50) {
    levelNumber = PASSWORD_STRENGTH_ENUM.STRONG;
  } else if (result > 25) {
    levelNumber = PASSWORD_STRENGTH_ENUM.COMMONLY;
  } else if (result > 0) {
    levelNumber = PASSWORD_STRENGTH_ENUM.WEAK;
  }
  return levelNumber;
}
/**
 * 密码加密
 * @param password 密码
 * @param publicKey 公钥
 */
export function passwordEncryption(password: string, publicKey: string): string {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  const encrypted = encrypt.encrypt(password);
  return encrypted || '';
}
